import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Skeleton, CircularProgress, Button, Collapse } from '@mui/material';
import { keyframes } from '@mui/system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link as RouterLink } from 'react-router-dom';
import FeatureCard from './home/FeatureCard';
import { HOME_FEATURES_DEFAULT } from '../data/homeFeaturesDefault';
import type { HomeFeaturesConfig } from '../types/homeFeatures';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface IntroductionSectionProps {
  loading?: boolean;
  /** フェーズ2で API から取得した設定を渡す。未指定なら草案デフォルトを使用 */
  config?: HomeFeaturesConfig;
}

const IntroductionSection: React.FC<IntroductionSectionProps> = ({ loading = false, config }) => {
  const cfg = config ?? HOME_FEATURES_DEFAULT;
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: { xs: 8, md: 12 } }}>
        <Box sx={{ width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 6 }}>
          <CircularProgress size={40} />
        </Box>
        <Skeleton variant="text" width={300} height={56} sx={{ mx: 'auto', mb: 2 }} />
        <Skeleton variant="text" width="70%" height={24} sx={{ mx: 'auto', mb: 8 }} />
        <Grid container spacing={6}>
          {[1, 2].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Skeleton variant="rounded" width={48} height={48} sx={{ mb: 2, borderRadius: 2 }} />
              <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="rounded" width="100%" height={200} sx={{ mt: 2, borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (!cfg.enabled) return null;

  const items = cfg.items.filter((i) => i.enabled !== false);

  // 先頭 featured_count 個をスポットライト行（大）、残りを bento グリッド。
  const featuredCount = Math.min(Math.max(0, cfg.featured_count), items.length);
  const featured = items.slice(0, featuredCount);
  const rest = items.slice(featuredCount);

  // 折りたたみ位置：枠に関係ない「合計表示数」visible_count。これを超えた分を「すべて見る」で展開。
  // 注目枠は常に表示するため、visible_count は featuredCount 未満に丸めない。
  const visibleCount = cfg.collapse_rest ? Math.max(featuredCount, cfg.visible_count) : items.length;
  const bentoVisibleCount = Math.max(0, visibleCount - featuredCount);
  const visibleRest = rest.slice(0, bentoVisibleCount);
  const hiddenRest = rest.slice(bentoVisibleCount);

  return (
    <>
      {/* ヒーロー画像（背景透過のPCはめ込みPNG。角丸・グラデ・アンビエント光は載せない・lg幅） */}
      <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ width: '100%', aspectRatio: '16/9', mb: { xs: 3, md: 5 } }}>
          <Box
            component="img"
            src="/image/TOP_image1.png"
            alt="PULSEの特徴"
            sx={{
              width: '100%',
              opacity: 0,
              animation: `${fadeIn} 1s ease-out 0.2s forwards`,
            }}
          />
        </Box>
      </Container>

      {/* 機能エリア：アンビエント光は全幅の親に敷き（lgコンテナ端で途切れさせない）、上にlg Containerを重ねる */}
      <Box sx={{ position: 'relative', mb: { xs: 6, md: 10 } }}>
        <Box
          aria-hidden
          // 不変条件: inset:0 の箱は四辺でクリップするので「四辺すべてで色が透明」でないと必ず切れる。
          // → 中心を上端より下げ(360px)、透明化(~274px)を上端(0)に届く前に完了させる。横も両端前に透明。
          // 単一の淡い光源。px固定なので機能展開でも動かない。
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            background: `radial-gradient(65% 380px at 50% 360px, ${theme.palette.primary.main}10, transparent 72%)`,
          })}
        />

        {/* 前面コンテンツ（lg幅） */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
      {/* セクション見出し */}
      <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: { xs: 6, md: 12 } }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: '2rem', md: '2.9rem' },
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            background: (theme) => `linear-gradient(160deg, ${theme.palette.text.primary} 30%, ${theme.palette.primary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {cfg.section_title}
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', lineHeight: 1.5, fontWeight: 400, fontSize: { xs: '0.85rem', md: '1.05rem' } }}>
          {cfg.section_subtitle}
        </Typography>
      </Box>

      {/* 注目枠：スポットライト行（枠なし・大きな余白で区切る） */}
      {featured.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 5, md: 8 },
            mb: rest.length ? { xs: 5, md: 9 } : 0,
          }}
        >
          {featured.map((item, idx) => (
            <FeatureCard key={item.id} item={item} variant="featured" imageRight={idx % 2 === 0} />
          ))}
        </Box>
      )}

      {/* 残り：bento グリッド */}
      {rest.length > 0 && (
        <>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {visibleRest.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <FeatureCard item={item} variant="compact" />
              </Grid>
            ))}
          </Grid>

          {hiddenRest.length > 0 && (
            <>
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 0 }}>
                  {hiddenRest.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <FeatureCard item={item} variant="compact" />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>

              <Box sx={{ textAlign: 'center', mt: { xs: 3, md: 4 } }}>
                <Button
                  onClick={() => setExpanded((v) => !v)}
                  endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  sx={{
                    borderRadius: '999px',
                    px: 3.5,
                    py: 1.25,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: 'transparent', color: 'primary.main' },
                  }}
                >
                  {expanded ? '閉じる' : `他の機能も見る`}
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {/* CTA（グラスピル・ライムは光として） */}
      <Box sx={{ textAlign: 'center', mt: { xs: 5, md: 8 } }}>
        <Button
          component={RouterLink}
          to="/login"
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            borderRadius: '999px',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontSize: '0.85rem',
            fontWeight: 600,
            color: 'primary.main',
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}26, rgba(255,255,255,0.04))`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: (theme) => `1px solid ${theme.palette.primary.main}3D`,
            boxShadow: (theme) => `0 4px 18px ${theme.palette.primary.main}1F, inset 0 1px 0 rgba(255,255,255,0.14)`,
            transition: 'all 0.25s ease',
            '& .MuiButton-endIcon': { transition: 'transform 0.25s ease' },
            '&:hover': {
              borderColor: (theme) => `${theme.palette.primary.main}80`,
              boxShadow: (theme) => `0 8px 28px ${theme.palette.primary.main}33, inset 0 1px 0 rgba(255,255,255,0.2)`,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}33, rgba(255,255,255,0.05))`,
              '& .MuiButton-endIcon': { transform: 'translateX(4px)' },
            },
          }}
        >
          今すぐライブラリを開設
        </Button>
      </Box>
        </Container>
      </Box>
    </>
  );
};

export default IntroductionSection;
